import {
  defaultPipeline,
  pipelineTemplateSchema,
  type ApplicationStage,
  type PipelineConfig,
  type PipelineTemplateRequest,
  type PipelineTemplateView,
} from '@nest-recruit/shared';

// ADMIN-01 (Pipelines A): the editor's draft, what Save sends, and what stops it. Pure, so each rule is tested alone.

export interface PipelineDraft {
  readonly name: string;
  readonly roleFamily: string;
  readonly isDefault: boolean;
  readonly pipeline: PipelineConfig;
}

/** A new pipeline starts from §L21's stages; the very first one is the default, since nothing else is yet. */
export function pipelineDraftOf(view: PipelineTemplateView | null, isFirst = false): PipelineDraft {
  return {
    name: view?.name ?? '',
    roleFamily: view?.roleFamily ?? '',
    isDefault: view?.isDefault ?? isFirst,
    pipeline: view?.pipeline ?? defaultPipeline(),
  };
}

export function pipelineBodyOf(draft: PipelineDraft): PipelineTemplateRequest {
  return { name: draft.name.trim(), roleFamily: draft.roleFamily.trim() || null, isDefault: draft.isDefault, pipeline: draft.pipeline };
}

/** The same pipeline, as Save would send it. */
export function isSameDraft(left: PipelineDraft, right: PipelineDraft): boolean {
  return JSON.stringify(pipelineBodyOf(left)) === JSON.stringify(pipelineBodyOf(right));
}

/** Families are one family whatever their capitals (migration 0070), as the server decides. */
export function familyKey(roleFamily: string | null): string {
  return (roleFamily ?? '').trim().toLowerCase();
}

/** Another pipeline that already covers this draft's role family, if any. */
export function familyClashOf(draft: PipelineDraft, pipelines: readonly PipelineTemplateView[], selfId: string | null): PipelineTemplateView | null {
  const key = familyKey(draft.roleFamily);
  if (key === '') return null;
  return pipelines.find((pipeline) => pipeline.id !== selfId && familyKey(pipeline.roleFamily) === key) ?? null;
}

export interface DraftProblem {
  readonly message: string;
  /** The stage the problem is on, so the editor can name it ("Interview 1: a stage needs a name."). */
  readonly stage: ApplicationStage | null;
}

/** The first thing the server would refuse, checked with its own rules before Save is pressed. */
export function pipelineProblemOf(draft: PipelineDraft): DraftProblem | null {
  const parsed = pipelineTemplateSchema.safeParse(pipelineBodyOf(draft));
  if (parsed.success) return null;
  const issue = parsed.error.issues[0];
  if (!issue) return null;
  return { message: issue.message, stage: stageAt(draft, issue.path) };
}

function stageAt(draft: PipelineDraft, path: readonly PropertyKey[]): ApplicationStage | null {
  const [section, list, index] = path;
  if (section !== 'pipeline' || typeof index !== 'number') return null;
  if (list === 'stages') return draft.pipeline.stages[index]?.stage ?? null;
  if (list === 'outcomes') return draft.pipeline.outcomes[index]?.stage ?? null;
  return null;
}
